import React, { useCallback } from 'react';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';

// Standard action names — kept in lock-step with backend verify checks.
export const RecaptchaActions = {
  otp: 'otp_send',
  registerVerify: 'register_verify',
  contact: 'contact_submit',
  sponsor: 'sponsor_enquiry',
  subscribe: 'newsletter_subscribe',
};

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

/**
 * Domains where the reCAPTCHA site key is actually registered.
 * Requests from any other hostname (e.g. Emergent preview URLs) skip reCAPTCHA
 * to avoid Google returning "Invalid domain for site key" — which forces the
 * backend to fail every credentialed request and breaks the OTP flow.
 * Update this list when a new production hostname is provisioned in the
 * reCAPTCHA admin console.
 */
const RECAPTCHA_ALLOWED_HOSTS = [
  'adauraawards.com',
  'www.adauraawards.com',
  'localhost',
];

const _isRecaptchaAllowed = () => {
  if (typeof window === 'undefined' || !window.location) return true;
  return RECAPTCHA_ALLOWED_HOSTS.includes(window.location.hostname);
};

/** Wraps the app. If no site key is configured, renders children unchanged
 *  (dev fail-open). Production deployments MUST set REACT_APP_RECAPTCHA_SITE_KEY. */
export const RecaptchaProvider = ({ children }) => {
  if (!SITE_KEY || !_isRecaptchaAllowed()) return children;
  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={SITE_KEY}
      scriptProps={{ async: true, defer: true, appendTo: 'head' }}
    >
      {children}
    </GoogleReCaptchaProvider>
  );
};

/** Hook returning an async (action) → token | null function.
 *  Token is null when reCAPTCHA isn't configured — callers should still send the
 *  request; the backend will skip verification in dev (fail-open). */
export const useRecaptchaToken = () => {
  const { executeRecaptcha } = useGoogleReCaptcha() || {};
  return useCallback(async (action) => {
    if (!SITE_KEY || !executeRecaptcha || !_isRecaptchaAllowed()) return null;
    try {
      return await executeRecaptcha(action);
    } catch (e) {
      console.warn('[recaptcha] execute failed', e);
      return null;
    }
  }, [executeRecaptcha]);
};

/** Build axios/fetch headers for a protected request. */
export const recaptchaHeaders = (token, action) => {
  if (!token) return {};
  return { 'X-Recaptcha-Token': token, 'X-Recaptcha-Action': action };
};
